import React from "react";

export interface ClientSearchFiltersState {
  year: number | "All";
  quarter: number | "All";
  applicationType: string;
  status: string;
  bonusRecipient: string;
  region: string;
  pageSize: number;
}

export interface ClientSearchFilterOption {
  value: string;
  label: string;
}

interface Props {
  filters: ClientSearchFiltersState;
  onChange: (next: ClientSearchFiltersState) => void;
  onSearch: () => void;
  onClear: () => void;
  isLoading?: boolean;
  selectedRole?: string;
  statusMode?: "all" | "recipientReviewOnly";
  applicationTypeOptions?: ClientSearchFilterOption[];
  statusOptions?: ClientSearchFilterOption[];
}

const ClientSearchFilters: React.FC<Props> = ({
  filters,
  onChange,
  onSearch,
  onClear,
  isLoading = false,
  selectedRole = "",
  statusMode = "all",
  applicationTypeOptions,
  statusOptions,
}) => {
  const normalizedRole = selectedRole.toUpperCase();

  const lockedApplicationType =
    normalizedRole === "CCM3MANAGER"
      ? "CCM3Manager"
      : ["CCM1", "CCM2", "CM3", "PM", "SPM"].includes(normalizedRole)
        ? normalizedRole
        : null;



  // CCM3 role can have both CCM3 and CCM3Manager app types
  const isCCM3Role = normalizedRole === "CCM3";

  const defaultApplicationTypeOptions: ClientSearchFilterOption[] = [
    { value: "All", label: "All" },
    { value: "AllCustomerCare", label: "All Customer Care" },
    { value: "AllConstruction", label: "All Construction" },
    { value: "AllCCM3Types", label: "All CCM3 Types" },
    { value: "CCM1", label: "CCM1" },
    { value: "CCM2", label: "CCM2" },
    { value: "CCM3", label: "CCM3" },
    { value: "CCM3Manager", label: "CCM3 Manager" },
    { value: "DCCM", label: "DCCM" },
    { value: "CM3", label: "CM3" },
    { value: "PM", label: "PM" },
    { value: "SPM", label: "SPM" },
  ];

  const ccm3ApplicationTypeOptions: ClientSearchFilterOption[] = [
    { value: "AllCCM3Types", label: "All CCM3 Types" },
    { value: "CCM3", label: "CCM3" },
    { value: "CCM3Manager", label: "CCM3 Manager" },
  ];

  const getApplicationTypeLabel = (value: string) =>
    value === "CCM3Manager"
      ? "CCM3 Manager"
      : value;

  const resolvedApplicationTypeOptions =
    applicationTypeOptions ??
    (isCCM3Role
      ? ccm3ApplicationTypeOptions
      : lockedApplicationType
        ? [{ value: lockedApplicationType, label: getApplicationTypeLabel(lockedApplicationType) }]
        : defaultApplicationTypeOptions);


  const recipientReviewOnlyStatusOption: ClientSearchFilterOption[] = [
    { value: "Bonus_Recipient_Review", label: "Recipient Review" },
  ];


  const defaultStatusOptions: ClientSearchFilterOption[] = [
    { value: "All", label: "All" },
    { value: "Draft", label: "Draft" },
    { value: "Submitted", label: "Submitted" },
    { value: "Bonus_Recipient_Review", label: "Recipient Review" },
    { value: "VP_Review", label: "VP Review" },
    { value: "Rejected", label: "Rejected" },
    { value: "Ops_Review", label: "Ops Review" },
    { value: "Approved", label: "Approved" },
    { value: "Payroll_Processing", label: "Payroll Processing" },
    { value: "Paid", label: "Paid" },
    { value: "Cancelled", label: "Cancelled" },
    { value: "SPM_Review", label: "SPM Review" },
    { value: "DCCM_Review", label: "DCCM Review" },
    { value: "DM_Review", label: "DM Review" },
  ];

  const resolvedStatusOptions =
    statusOptions ?? (statusMode === "recipientReviewOnly" ? recipientReviewOnlyStatusOption : defaultStatusOptions);

  const resolvedApplicationType =
    resolvedApplicationTypeOptions.some(
      option => option.value === filters.applicationType
    )
      ? filters.applicationType
      : resolvedApplicationTypeOptions[0]?.value ?? "";

  const resolvedStatus =
    resolvedStatusOptions.some(
      option => option.value === filters.status
    )
      ? filters.status
      : resolvedStatusOptions[0]?.value ?? "";

  const prevQuarter = (() => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentQuarter = Math.floor(currentDate.getMonth() / 3) + 1;
    return currentQuarter === 1
      ? { year: currentYear - 1, quarter: 4 }
      : { year: currentYear, quarter: currentQuarter - 1 };
  })();

  const baseYearOptions = [prevQuarter.year, prevQuarter.year - 1, prevQuarter.year - 2];
  const yearOptions =
    typeof filters.year === "number" && !baseYearOptions.includes(filters.year)
      ? [...baseYearOptions, filters.year].sort((a, b) => b - a)
      : baseYearOptions;
  const maxQuarter = filters.year === "All" ? 4 : filters.year < prevQuarter.year ? 4 : prevQuarter.quarter;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Search Filters</h2>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSearch();
        }}
        className="space-y-4"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
            <select
              value={filters.year}
              onChange={(e) =>
                onChange({
                  ...filters,
                  year: e.target.value === "All" ? "All" : parseInt(e.target.value, 10),
                  quarter: e.target.value === "All" ? "All" : filters.quarter,
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="All">All</option>
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Quarter</label>
            <select
              value={filters.quarter}
              disabled={filters.year === "All"}
              onChange={(e) =>
                onChange({
                  ...filters,
                  quarter: e.target.value === "All" ? "All" : parseInt(e.target.value, 10),
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:bg-gray-100"
            >
              <option value="All">All</option>
              {[1, 2, 3, 4].map((q) => (
                <option key={q} value={q} disabled={q > maxQuarter}>
                  Q{q}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Application Type</label>
            <select
              value={resolvedApplicationType}
              onChange={(e) =>
                onChange({
                  ...filters,
                  applicationType: e.target.value,
                })
              }
              disabled={Boolean(lockedApplicationType)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              {resolvedApplicationTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={resolvedStatus}
              onChange={(e) => onChange({ ...filters, status: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              {resolvedStatusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bonus Recipient</label>
            <input
              type="text"
              value={filters.bonusRecipient}
              onChange={(e) => onChange({ ...filters, bonusRecipient: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="Name or Email"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Region</label>
            <select
              value={filters.region}
              onChange={(e) => onChange({ ...filters, region: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="All">All</option>
              <optgroup label="Texas">
                <option value="Austin">Austin</option>
                <option value="DFW">DFW</option>
                <option value="Houston">Houston</option>
                <option value="San Antonio">San Antonio</option>
              </optgroup>
              <optgroup label="">
                <option value="Other">Other</option>
              </optgroup>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Results per Page</label>
            <select
              value={filters.pageSize}
              onChange={(e) => onChange({ ...filters, pageSize: parseInt(e.target.value, 10) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              {[10, 25, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClear} className="px-6 py-2 bg-gray-100 text-gray-700 rounded-md">
            Clear
          </button>
          <button type="submit" disabled={isLoading} className="px-6 py-2 bg-primary-600 text-white rounded-md">
            {isLoading ? "Searching..." : "Search"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ClientSearchFilters;
